import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, Upload, Save, Trash2 } from "lucide-react";
import IntroSplash from "@/components/IntroSplash";
import StickySaveBar from "@/components/admin/StickySaveBar";
import { ExportImportButtons } from "./components/ExportImportButtons.jsx";

const ENTER_OPTIONS = [
  { value: "zoom_in", label: "Zoom in (predefinido)" },
  { value: "fade", label: "Fade" },
  { value: "drop", label: "Cair de cima" },
  { value: "pop", label: "Pop" },
  { value: "slide_up", label: "Subir (slide-up)" },
  { value: "slide_left", label: "Entrar pela esquerda" },
  { value: "slide_right", label: "Entrar pela direita" },
  { value: "bounce_in", label: "Bounce (saltita)" },
  { value: "spin_in", label: "Spin (rodopia)" },
  { value: "flip_in", label: "Flip 3D" },
  { value: "blur_in", label: "Blur-in (foca)" },
  { value: "elastic_in", label: "Elástico" },
  { value: "swing_in", label: "Swing (balanço)" },
];
const EXIT_OPTIONS = [
  { value: "explode", label: "Explosão (predefinido)" },
  { value: "backdraft", label: "💥 Backdraft (implode e retroexplode)" },
  { value: "zoom_out", label: "Zoom out" },
  { value: "fade", label: "Fade" },
  { value: "drop", label: "Cair" },
  { value: "morph", label: "✨ Morph (suga e transforma no logo)" },
  { value: "slide_up_out", label: "Sair para cima" },
  { value: "slide_left_out", label: "Sair para a esquerda" },
  { value: "slide_right_out", label: "Sair para a direita" },
  { value: "spin_out", label: "Spin-out (rodopia e some)" },
  { value: "flip_out", label: "Flip 3D" },
  { value: "blur_out", label: "Blur-out (desfoca)" },
  { value: "shrink_out", label: "Encolher e rodar" },
  { value: "pixelate_out", label: "Pixelizar (TV antiga)" },
];

export default function AdminIntro() {
  const [form, setForm] = useState(null);
  const [previewKey, setPreviewKey] = useState(0); // force remount of IntroSplash preview

  useEffect(() => {
    api.get("/intro/settings").then((r) => setForm(r.data));
  }, []);

  if (!form) return <div className="p-6">A carregar…</div>;

  const upd = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onUpload = (langKey) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("Imagem demasiado grande (máx 3 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => upd({ [langKey]: ev.target.result });
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      await api.put("/intro/settings", form);
      toast.success("Configurações guardadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar");
    }
  };

  const triggerPreview = () => {
    sessionStorage.removeItem("supertmatik_intro_seen");
    localStorage.removeItem("supertmatik_intro_visited");
    setPreviewKey((k) => k + 1);
  };

  const ImgUpload = ({ langKey, label }) => (
    <div className="space-y-1">
      <Label>{label}</Label>
      <div className="flex items-center gap-3">
        {form[langKey] ? (
          <div className="relative">
            <img src={form[langKey]} alt={langKey} className="h-16 w-16 object-contain bg-slate-900 rounded p-1" />
            <button
              data-testid={`remove-${langKey}`}
              onClick={() => upd({ [langKey]: "" })}
              className="absolute -top-2 -right-2 bg-rose-500 text-white rounded-full p-0.5 hover:bg-rose-600"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="h-16 w-16 bg-slate-100 rounded grid place-items-center text-slate-400 text-xs">—</div>
        )}
        <label className="cursor-pointer inline-flex items-center gap-1 px-3 py-2 bg-sky-100 text-sky-700 rounded border border-sky-300 hover:bg-sky-200 text-sm font-medium">
          <Upload className="w-4 h-4" /> Carregar
          <input type="file" accept="image/*" className="hidden" data-testid={`upload-${langKey}`} onChange={onUpload(langKey)} />
        </label>
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-3xl font-[Outfit] font-semibold text-slate-900">Intro Splash</h1>
        <div className="flex gap-2 flex-wrap">
          <ExportImportButtons
            exportUrl="/intro/settings/export"
            importUrl="/intro/settings/import"
            filePrefix="intro-settings"
            onImported={() => window.location.reload()}
            testidPrefix="intro"
          />
          <Button data-testid="btn-preview-intro" variant="outline" onClick={triggerPreview}>
            <Eye className="w-4 h-4 mr-2" /> Pré-visualizar
          </Button>
          <Button data-testid="btn-save-intro" onClick={save} className="bg-slate-900">
            <Save className="w-4 h-4 mr-2" /> Guardar
          </Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 p-6 space-y-6 max-w-3xl">
        {/* Enable */}
        <div className="flex items-center gap-3">
          <Switch checked={form.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="intro-enabled" />
          <Label className="m-0">Ativar intro</Label>
        </div>

        {/* Frequency */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Frequência</Label>
            <Select value={form.frequency} onValueChange={(v) => upd({ frequency: v })}>
              <SelectTrigger data-testid="intro-frequency"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="session">Uma vez por sessão</SelectItem>
                <SelectItem value="first_visit">Só na primeira visita (não repete)</SelectItem>
                <SelectItem value="always">Sempre que entra</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3">
            <Switch checked={form.wait_for_click} onCheckedChange={(v) => upd({ wait_for_click: v })} data-testid="intro-wait-click" />
            <Label className="m-0 pb-2">Aguardar clique para sair</Label>
          </div>
        </div>

        {/* Duration + Dim */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Duração (segundos): {form.duration_sec}</Label>
            <input type="range" min={1} max={10} step={0.5} value={form.duration_sec} onChange={(e) => upd({ duration_sec: parseFloat(e.target.value) })} className="w-full" data-testid="intro-duration" />
          </div>
          <div>
            <Label>Escurecimento do fundo: {form.dim_pct}%</Label>
            <input type="range" min={0} max={100} value={form.dim_pct} onChange={(e) => upd({ dim_pct: +e.target.value })} className="w-full" data-testid="intro-dim" />
          </div>
          <div>
            <Label>Cor do overlay</Label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={form.dim_color || "#000000"}
                onChange={(e) => upd({ dim_color: e.target.value })}
                className="h-9 w-14 rounded border border-slate-300 p-1"
                data-testid="intro-dim-color"
              />
              <input
                type="text"
                value={form.dim_color || "#000000"}
                onChange={(e) => upd({ dim_color: e.target.value })}
                placeholder="#000000"
                className="flex-1 h-9 rounded border border-slate-300 px-2 text-sm font-mono"
                data-testid="intro-dim-color-hex"
              />
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Combinada com “Escurecimento do fundo” para gerar o rgba do overlay (a homepage renderiza por trás).</p>
          </div>
        </div>

        {/* Effects — UNIFIED: a single pair controls the whole splash (background, image, button, title).
            Internally we mirror the global values onto the title-specific fields so existing IntroSplash
            override logic falls through to one consistent behaviour. */}
        <div className="border-t border-slate-200 pt-4">
          <div className="text-sm font-bold text-slate-700 mb-1">Efeitos do Splash</div>
          <p className="text-[11px] text-slate-500 mb-3">Um único efeito de entrada e saída aplica-se ao splash inteiro (fundo, imagem do título e botão).</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Efeito de entrada</Label>
              <Select
                value={form.enter_effect}
                onValueChange={(v) => upd({
                  enter_effect: v,
                  // Force unified behaviour: clear title overrides so the IntroSplash falls back to global.
                  title_enter_effect: null,
                  title_use_global: true,
                })}
              >
                <SelectTrigger data-testid="intro-enter-effect"><SelectValue /></SelectTrigger>
                <SelectContent>{ENTER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Efeito de saída</Label>
              <Select
                value={form.exit_effect}
                onValueChange={(v) => upd({
                  exit_effect: v,
                  title_exit_effect: null,
                  title_use_global: true,
                })}
              >
                <SelectTrigger data-testid="intro-exit-effect"><SelectValue /></SelectTrigger>
                <SelectContent>{EXIT_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Atraso de entrada: <span className="font-mono">{form.title_enter_delay_ms ?? 0} ms</span></Label>
              <input type="range" min={0} max={10000} step={100} value={form.title_enter_delay_ms ?? 0} onChange={(e) => upd({ title_enter_delay_ms: +e.target.value })} className="w-full" data-testid="title-enter-delay" />
              <p className="text-[11px] text-slate-500">Tempo antes do efeito de entrada começar.</p>
            </div>
            <div>
              <Label>Atraso de saída: <span className="font-mono">{form.title_exit_delay_ms ?? 0} ms</span></Label>
              <input type="range" min={0} max={10000} step={100} value={form.title_exit_delay_ms ?? 0} onChange={(e) => upd({ title_exit_delay_ms: +e.target.value })} className="w-full" data-testid="title-exit-delay" />
              <p className="text-[11px] text-slate-500">Tempo antes do efeito de saída começar.</p>
            </div>
          </div>
        </div>

        {/* Language buttons (PT/EN/ES flags) */}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <div className="flex items-center gap-3">
            <Switch
              checked={form.show_language_buttons !== false}
              onCheckedChange={(v) => upd({ show_language_buttons: v })}
              data-testid="intro-show-lang-buttons"
            />
            <Label className="m-0">Mostrar botões de idioma (🇵🇹 🇬🇧 🇪🇸)</Label>
          </div>
          <p className="text-[11px] text-slate-500 -mt-1">
            Aparecem imediatamente abaixo da imagem no Intro Splash. O visitante escolhe o idioma e é conduzido para a Homepage já traduzida.
          </p>
        </div>

        {/* Button */}
        <div className="border-t border-slate-200 pt-4 space-y-3">
          <div className="flex items-center gap-3">
            <Switch checked={form.show_button} onCheckedChange={(v) => upd({ show_button: v })} data-testid="intro-show-button" />
            <Label className="m-0">Mostrar botão Entrar</Label>
          </div>
          {form.show_button && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Texto PT</Label><Input value={form.button_text_pt} onChange={(e) => upd({ button_text_pt: e.target.value })} data-testid="btn-text-pt" /></div>
                <div><Label>Texto EN</Label><Input value={form.button_text_en} onChange={(e) => upd({ button_text_en: e.target.value })} data-testid="btn-text-en" /></div>
                <div><Label>Texto ES</Label><Input value={form.button_text_es} onChange={(e) => upd({ button_text_es: e.target.value })} data-testid="btn-text-es" /></div>
              </div>
              <div>
                <Label>Cor do botão</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.button_color || "#CFE40F"}
                    onChange={(e) => upd({ button_color: e.target.value })}
                    className="h-10 w-16 rounded border cursor-pointer"
                    data-testid="btn-color-picker"
                  />
                  <Input
                    value={form.button_color || "#CFE40F"}
                    onChange={(e) => upd({ button_color: e.target.value })}
                    className="flex-1 font-mono"
                    data-testid="btn-color-hex"
                  />
                  <div
                    className="px-4 py-2 rounded-full border-2 border-white shadow font-bold text-sm"
                    style={{ background: form.button_color || "#CFE40F" }}
                  >
                    {form.button_text_pt || "Entrar"}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Images per language */}
        <div className="border-t border-slate-200 pt-4">
          <div className="text-sm font-bold text-slate-700 mb-3">Imagem do título (substitui texto)</div>
          <div className="grid grid-cols-3 gap-4">
            <ImgUpload langKey="image_pt" label="🇵🇹 Português" />
            <ImgUpload langKey="image_en" label="🇬🇧 English" />
            <ImgUpload langKey="image_es" label="🇪🇸 Español" />
          </div>
          <p className="text-xs text-slate-500 mt-2">Se uma imagem estiver em falta, usa a do PT como fallback. Sem imagem, mostra o texto definido em baixo.</p>

          {/* Fallback text per language (when no image is loaded) */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            <div><Label>Texto PT (se sem imagem)</Label><Input value={form.title_text_pt || ""} onChange={(e) => upd({ title_text_pt: e.target.value })} placeholder="Intro" data-testid="title-text-pt" /></div>
            <div><Label>Texto EN (se sem imagem)</Label><Input value={form.title_text_en || ""} onChange={(e) => upd({ title_text_en: e.target.value })} placeholder="Intro" data-testid="title-text-en" /></div>
            <div><Label>Texto ES (se sem imagem)</Label><Input value={form.title_text_es || ""} onChange={(e) => upd({ title_text_es: e.target.value })} placeholder="Intro" data-testid="title-text-es" /></div>
          </div>
        </div>
      </div>

      {/* Preview - mount IntroSplash with fresh key */}
      <div key={previewKey} />
      {previewKey > 0 && <IntroSplash forcePreview />}

      <StickySaveBar>
        <Button data-testid="btn-preview-intro-sticky" variant="outline" onClick={triggerPreview}>
          <Eye className="w-4 h-4 mr-2" /> Pré-visualizar
        </Button>
        <Button data-testid="btn-save-intro-sticky" onClick={save} className="bg-slate-900">
          <Save className="w-4 h-4 mr-2" /> Guardar
        </Button>
      </StickySaveBar>
    </div>
  );
}
